// DateRangePicker — Today / Yesterday / 7d / 30d / Custom button row used
// by the v5 audit-log filter bar. The "Custom" button opens a popover with
// two native date inputs; this is the smallest dependency-free spelling
// that matches the mockup intent without pulling in a calendar library.
//
// Range semantics are inclusive on both ends and always in UTC (matches the
// BFF contract in LLD §8.1 — `from`/`to` are ISO dates, not timestamps).

import { useEffect, useRef, useState } from "react";
import type { RangePreset } from "./types";
import "./Audit.css";

export interface DateRange {
  /** yyyy-MM-dd (UTC). */
  from: string;
  /** yyyy-MM-dd (UTC). */
  to: string;
}

export interface DateRangePickerProps {
  preset: RangePreset;
  range: DateRange;
  onChange: (preset: RangePreset, range: DateRange) => void;
  /** Hard cap (UI-side warning). Server enforces 30d for export, 90d for list. */
  maxDays?: number;
}

function todayIsoUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftIso(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function diffDaysInclusive(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  return Math.floor((b - a) / 86_400_000) + 1;
}

export function presetToRange(preset: RangePreset, current: DateRange): DateRange {
  const today = todayIsoUtc();
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const y = shiftIso(today, -1);
      return { from: y, to: y };
    }
    case "7d":
      return { from: shiftIso(today, -6), to: today };
    case "30d":
      return { from: shiftIso(today, -29), to: today };
    case "custom":
      return current;
  }
}

export function DateRangePicker(props: DateRangePickerProps): JSX.Element {
  const { preset, range, onChange, maxDays = 90 } = props;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [draftFrom, setDraftFrom] = useState(range.from);
  const [draftTo, setDraftTo] = useState(range.to);

  useEffect(() => {
    if (popoverOpen) {
      setDraftFrom(range.from);
      setDraftTo(range.to);
    }
  }, [popoverOpen, range.from, range.to]);

  useEffect(() => {
    if (!popoverOpen) return;
    function handler(ev: MouseEvent) {
      if (!containerRef.current) return;
      if (containerRef.current.contains(ev.target as Node)) return;
      setPopoverOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [popoverOpen]);

  function pickPreset(p: RangePreset) {
    if (p === "custom") {
      setPopoverOpen((cur) => !cur);
      return;
    }
    setPopoverOpen(false);
    onChange(p, presetToRange(p, range));
  }

  function applyCustom() {
    if (draftFrom > draftTo) {
      // Swap to avoid an inverted range.
      onChange("custom", { from: draftTo, to: draftFrom });
    } else {
      onChange("custom", { from: draftFrom, to: draftTo });
    }
    setPopoverOpen(false);
  }

  const days = diffDaysInclusive(draftFrom, draftTo);
  const overCap = days > maxDays;

  function pillClass(p: RangePreset) {
    return preset === p
      ? "rb-audit__fbtn rb-audit__fbtn--active"
      : "rb-audit__fbtn";
  }

  return (
    <div ref={containerRef} style={{ position: "relative", display: "contents" }}>
      <span className="rb-audit__flabel">Range</span>
      <button
        type="button"
        className={pillClass("today")}
        onClick={() => pickPreset("today")}
      >
        Today
      </button>
      <button
        type="button"
        className={pillClass("yesterday")}
        onClick={() => pickPreset("yesterday")}
      >
        Yesterday
      </button>
      <button
        type="button"
        className={pillClass("7d")}
        onClick={() => pickPreset("7d")}
      >
        Last 7 days
      </button>
      <button
        type="button"
        className={pillClass("30d")}
        onClick={() => pickPreset("30d")}
      >
        Last 30 days
      </button>
      <div style={{ position: "relative" }}>
        <button
          type="button"
          className={pillClass("custom")}
          onClick={() => pickPreset("custom")}
          aria-expanded={popoverOpen}
          aria-haspopup="dialog"
        >
          Custom…
          {preset === "custom" && (
            <span className="rb-audit__fbtn-count">
              {diffDaysInclusive(range.from, range.to)}d
            </span>
          )}
        </button>
        {popoverOpen && (
          <div
            className="rb-audit__popover rb-audit__daterange"
            role="dialog"
            aria-label="Custom date range"
          >
            <div className="rb-audit__daterange-head">
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                {draftFrom} → {draftTo}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  color: overCap ? "#d4231a" : "#1f9d55",
                  fontWeight: 600,
                }}
              >
                {days} day{days === 1 ? "" : "s"}
              </span>
            </div>
            <div className="rb-audit__daterange-row">
              <label htmlFor="rb-audit-from">From</label>
              <input
                id="rb-audit-from"
                type="date"
                value={draftFrom}
                max={todayIsoUtc()}
                onChange={(e) => setDraftFrom(e.target.value)}
              />
            </div>
            <div className="rb-audit__daterange-row" style={{ marginTop: 8 }}>
              <label htmlFor="rb-audit-to">To</label>
              <input
                id="rb-audit-to"
                type="date"
                value={draftTo}
                max={todayIsoUtc()}
                onChange={(e) => setDraftTo(e.target.value)}
              />
            </div>
            {overCap && (
              <div className="rb-audit__daterange-warn">
                Range exceeds {maxDays} days — exports cap at 30 days; the
                list may show truncated results.
              </div>
            )}
            <div className="rb-audit__popover-footer" style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setPopoverOpen(false)}
              >
                Cancel
              </button>
              <button type="button" onClick={applyCustom}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
